import { authenticationLoaders } from 'domain/loaders/authenticationLoaders';
import { userLoaders } from 'domain/loaders/userLoaders';
import { SuccessResponse } from 'domain/models/response-model';
import { UserStatus } from 'domain/models/user-status';
import { Disposers, HttpPromiseState, StringControl, toastrHelper, validators } from 'lib/helpers';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { routes } from 'pages/Routes';
import AvatarEditor from 'react-avatar-editor';

export class StudentEditProfileStore {  
    firstName: StringControl;
    lastName: StringControl;

    @observable.ref
    saveState: HttpPromiseState<SuccessResponse> | null = null;
    @observable
    redirectTo: string | null = null;
    @observable
    avatar: File | null = null;
    @observable
    croppedImage: string | null = null;
    @observable
    showAvatarEditor: boolean = false;
    editor: AvatarEditor | null = null;

    readonly disposers = new Disposers();


    constructor(readonly userId: number) {
        makeObservable(this);
        this.firstName = new StringControl('firstName', '', validators.required);
        this.lastName = new StringControl('lastName', '', validators.required);
        this.setup();
    }

    @action
    loadUser = () => {
        authenticationLoaders.whoAmI()
            .then((userStatus) => {
                if(!userStatus.isLoggedIn){
                    toastrHelper.error("Student not logged");
                    return;
                }
                    
                const user = userStatus.user;

                if(user == null) return;

                this.firstName.setValue(user.FirstName);
                this.lastName.setValue(user.LastName);

                if(user.ProfilePhotoUrl)
                    this.croppedImage = user.ProfilePhotoUrl;
            });
    };

    @action
    save = () => {
        if (this.saveState?.isPending === true) return;
        if (this.invalid) return;

        const formData = new FormData();
        formData.append('FirstName', this.firstName.value);
        formData.append('LastName', this.lastName.value);
        
        if (this.avatar) {
            formData.append('Avatar', this.avatar);
        }

        this.saveState = userLoaders.updateStudentProfile(formData);
    };

    @action
    cancel = () => {
        this.redirectTo = routes.Dashboard;
    };

    @action
    setAvatar = (blobFile: Blob | File) => {
        let file: File;
        
        if (blobFile instanceof File) {
            file = blobFile;
        } else {
            file = new File([blobFile], `student-avatar${this.getCorrectFileType(blobFile.type)}`, { type: blobFile.type });
        }

        this.avatar = file;        
    };

    @action
    saveCroppedAvatar = (imageDataUrl: string) => {
        this.croppedImage = imageDataUrl;
        this.showAvatarEditor = false;
    };

    dispose = () => {
        this.disposers.dispose();
    };

    @computed
    get invalid(): boolean {
        return this.firstName.invalid || this.lastName.invalid;
    }

    private getCorrectFileType(type: string){
        switch(type) {
            case 'image/png':
                return '.png';
            case 'image/jpeg':
                return '.jpeg';
            case 'image/jpg':
                return '.jpg';
        }
    }

    private setup() {
        this.disposers.add(reaction(
            () => this.saveState?.state,
            this.onSaveStateChange
        ));
    }

    @action
    private onSaveStateChange = () => {
        if (this.saveState?.isResolved !== true) return;
        toastrHelper.success(this.saveState.value);
        window.location.reload();
    };
}
